import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { Power as PowerIcon, PersonAdd as PersonIcon, ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import { fetchTodaysBookings } from '../api/bookings';
import { fetchConfigurations } from '../api/configurations';
import { getOccupancyColor } from '../utils';

const TodayBookingsList = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [occupancyPercent, setOccupancyPercent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [thresholds, setThresholds] = useState({ high: 75, medium: 50 });

  const loadThresholds = useCallback(async () => {
    try {
      const data = await fetchConfigurations();
      setThresholds({
        high: parseFloat(data.HIGH_OCCUPANCY || 75),
        medium: parseFloat(data.MEDIUM_OCCUPANCY || 50),
      });
    } catch (err) {
      console.error('Error fetching configuration thresholds:', err);
    }
  }, []);

  const loadTodaysBookings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchTodaysBookings();
      setBookings(data.bookings || []);
      setOccupancyPercent(data.occupancy_percent || 0);
    } catch (err) {
      console.error('Error fetching today\'s bookings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThresholds();
    loadTodaysBookings();
  }, [loadThresholds, loadTodaysBookings]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('bookings.today_parking_bookings')}
      </Typography>

      <Box sx={{ mt: 2, mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={occupancyPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.300',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getOccupancyColor(occupancyPercent, thresholds),
            },
          }}
        />
      </Box>

      {bookings.length === 0 ? (
        <Typography color="text.secondary">
          No bookings for today
        </Typography>
      ) : (
        <List dense>
          {bookings.map((booking, index) => (
            <React.Fragment key={booking.id}>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>
                        Slot {booking.slot_number}
                      </span>
                      {booking.slot_has_charger && (
                        <PowerIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      )}
                      {booking.report && (
                        <ReportProblemIcon fontSize="small" sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {booking.is_guest && (
                          <PersonIcon fontSize="small" color="primary" />
                        )}
                        <span>
                          {booking.is_guest 
                            ? `${booking.guestName} (${booking.guestCompany})`
                            : booking.email
                          }
                        </span>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {booking.address}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {index < bookings.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TodayBookingsList; 